/**
 * Renders one chat message with markdown, optional entity links, and preview popover.
 * Reuses MarkdownRenderer (same as Vera / GenericChatWindow). See docs/ADMIN_CHAT_ENTITY_LINKS.md
 */
import { Box, Popover } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import type { Components } from 'react-markdown';
import { MarkdownRenderer } from '../MarkdownRenderer';
import useScannedChatMessageSegments from '../../hooks/admin/useScannedChatMessageSegments';
import { ChatLinkTarget, ChatTextSegment, findChatLinksInText } from '../../utils/chatLinkCorpus';
import ChatLinkTargetPreviewCarousel from './ChatLinkTargetPreviewCarousel';

interface ChatLinkifiedMessageTextProps {
  messageId: string;
  text: string;
  corpus: ChatLinkTarget[] | null;
  /** `inline` for chat bubbles; `block` for AI assist / multi-paragraph markdown */
  markdownVariant?: 'inline' | 'block';
}

const messageTypographySx = {
  fontSize: '0.875rem',
  lineHeight: 1.5,
  wordBreak: 'break-word',
  '& .vera-markdown-table': {
    fontSize: '0.75rem',
  },
  '& p:last-child': { mb: 0 },
  '& ul, & ol': { my: 0.5, pl: 2.5 },
};

/**
 * Trailing/leading spaces collapse between adjacent inline markdown and link spans.
 * Use non-breaking spaces in segment text so "about wantirna" keeps the gap.
 */
export function preserveSegmentSpacesAroundLinks(segments: ChatTextSegment[]): ChatTextSegment[] {
  return segments.map((segment, index) => {
    if (segment.type !== 'text') return segment;

    let value = segment.value;
    if (segments[index + 1]?.type === 'link') {
      value = value.replace(/ +$/g, (spaces) => '\u00a0'.repeat(spaces.length));
    }
    if (segments[index - 1]?.type === 'link') {
      value = value.replace(/^ +/g, (spaces) => '\u00a0'.repeat(spaces.length));
    }
    return { ...segment, value };
  });
}

function linkAriaLabel(value: string, targets: ChatLinkTarget[]): string {
  if (targets.length <= 1) {
    return value;
  }
  return `${value} (${targets.length} matches)`;
}

function renderLinkifiedText(
  text: string,
  corpus: ChatLinkTarget[],
  messageId: string,
  keyPrefix: string,
  onLinkClick: (event: React.MouseEvent<HTMLElement>, targets: ChatLinkTarget[]) => void,
): React.ReactNode {
  const textSegments = preserveSegmentSpacesAroundLinks(findChatLinksInText(text, corpus));
  if (textSegments.length === 1 && textSegments[0].type === 'text') {
    return text;
  }

  return textSegments.map((segment, index) => {
    if (segment.type === 'text') {
      return <React.Fragment key={`${keyPrefix}-text-${index}`}>{segment.value}</React.Fragment>;
    }

    const linkTargets = segment.targets ?? [];

    return (
      <Box
        key={`${keyPrefix}-link-${index}`}
        component="span"
        role="button"
        tabIndex={0}
        aria-label={linkAriaLabel(segment.value, linkTargets)}
        data-chat-link-target={linkTargets.map((target) => target.id).join(',')}
        onClick={(event) => {
          if (linkTargets.length > 0) {
            onLinkClick(event, linkTargets);
          }
        }}
        onKeyDown={(event) => {
          if ((event.key === 'Enter' || event.key === ' ') && linkTargets.length > 0) {
            event.preventDefault();
            onLinkClick(event as unknown as React.MouseEvent<HTMLElement>, linkTargets);
          }
        }}
        sx={{
          color: 'primary.main',
          textDecoration: 'underline',
          textDecorationStyle: 'dotted',
          cursor: 'pointer',
        }}
      >
        {segment.value}
      </Box>
    );
  });
}

function linkifyMarkdownChildren(
  children: React.ReactNode,
  corpus: ChatLinkTarget[],
  messageId: string,
  keyPrefix: string,
  onLinkClick: (event: React.MouseEvent<HTMLElement>, targets: ChatLinkTarget[]) => void,
): React.ReactNode {
  return React.Children.map(children, (child, index) => {
    const childKey = `${keyPrefix}-${index}`;

    if (typeof child === 'string') {
      return renderLinkifiedText(child, corpus, messageId, childKey, onLinkClick);
    }

    if (Array.isArray(child)) {
      return linkifyMarkdownChildren(child, corpus, messageId, childKey, onLinkClick);
    }

    if (React.isValidElement<{ children?: React.ReactNode }>(child)) {
      if (child.props.children == null) {
        return child;
      }

      return React.cloneElement(child, {
        children: linkifyMarkdownChildren(
          child.props.children,
          corpus,
          messageId,
          childKey,
          onLinkClick,
        ),
      });
    }

    return child;
  });
}

function createBlockLinkComponents(
  corpus: ChatLinkTarget[],
  messageId: string,
  onLinkClick: (event: React.MouseEvent<HTMLElement>, targets: ChatLinkTarget[]) => void,
) {
  const wrap =
    (Tag: keyof JSX.IntrinsicElements) =>
    ({ children, ...props }: React.PropsWithChildren<Record<string, unknown>>) =>
      React.createElement(
        Tag,
        props,
        linkifyMarkdownChildren(children, corpus, messageId, Tag, onLinkClick),
      );

  return {
    p: wrap('p'),
    li: wrap('li'),
    strong: wrap('strong'),
    em: wrap('em'),
  };
}

const ChatLinkifiedMessageText = React.memo(function ChatLinkifiedMessageText({
  messageId,
  text,
  corpus,
  markdownVariant = 'inline',
}: ChatLinkifiedMessageTextProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeTargets, setActiveTargets] = useState<ChatLinkTarget[]>([]);
  const rawSegments = useScannedChatMessageSegments(messageId, text, corpus);
  const segments = useMemo(() => preserveSegmentSpacesAroundLinks(rawSegments), [rawSegments]);

  const handleLinkClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, targets: ChatLinkTarget[]) => {
      event.preventDefault();
      setAnchorEl(event.currentTarget);
      setActiveTargets(targets);
    },
    [],
  );

  const isPlainMarkdown = useMemo(
    () => segments.length === 1 && segments[0].type === 'text',
    [segments],
  );

  const blockLinkComponents = useMemo(() => {
    if (markdownVariant !== 'block' || !corpus?.length) {
      return undefined;
    }

    return createBlockLinkComponents(corpus, messageId, handleLinkClick);
  }, [corpus, handleLinkClick, markdownVariant, messageId]);

  const handleClose = () => {
    setAnchorEl(null);
    setActiveTargets([]);
  };

  const renderedMessage =
    markdownVariant === 'block' ? (
      <MarkdownRenderer variant="block" components={blockLinkComponents as unknown as Components}>
        {text}
      </MarkdownRenderer>
    ) : isPlainMarkdown ? (
      <MarkdownRenderer variant={markdownVariant}>{text}</MarkdownRenderer>
    ) : (
      segments.map((segment, index) => {
        if (segment.type === 'text') {
          return (
            <Box key={`${messageId}-text-${index}`} component="span" sx={{ display: 'inline' }}>
              <MarkdownRenderer variant="inline">{segment.value}</MarkdownRenderer>
            </Box>
          );
        }

        const linkTargets = segment.targets ?? [];

        return (
          <Box
            key={`${messageId}-link-${index}`}
            component="span"
            role="button"
            tabIndex={0}
            aria-label={linkAriaLabel(segment.value, linkTargets)}
            data-chat-link-target={linkTargets.map((target) => target.id).join(',')}
            onClick={(event) => {
              if (linkTargets.length > 0) {
                handleLinkClick(event, linkTargets);
              }
            }}
            onKeyDown={(event) => {
              if ((event.key === 'Enter' || event.key === ' ') && linkTargets.length > 0) {
                event.preventDefault();
                handleLinkClick(event as unknown as React.MouseEvent<HTMLElement>, linkTargets);
              }
            }}
            sx={{
              color: 'primary.main',
              textDecoration: 'underline',
              textDecorationStyle: 'dotted',
              cursor: 'pointer',
            }}
          >
            {segment.value}
          </Box>
        );
      })
    );

  return (
    <>
      <Box
        sx={{
          ...messageTypographySx,
          // Keep ordinary spaces inside each markdown segment; nbsp handles gaps before links.
          whiteSpace: 'pre-wrap',
        }}
        data-message-id={messageId}
      >
        {renderedMessage}
      </Box>

      <Popover
        open={Boolean(anchorEl && activeTargets.length > 0)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {activeTargets.length > 0 && (
          <ChatLinkTargetPreviewCarousel targets={activeTargets} onPreviewNavigate={handleClose} />
        )}
      </Popover>
    </>
  );
});

export default ChatLinkifiedMessageText;
